import { useMemo, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import { listRuntimeLensSpecs, saveRuntimeLensSpec, deleteRuntimeLensSpec } from "../../lib/xrayRuntimeLensStore.js";
import { compileRuntimeLens } from "../../lib/xrayRuntimeLens.js";
import { readLensOverBodies } from "../../lib/xrayFactoryRun.js";
import { emitFromSpec } from "../../lib/xrayEmissions.js";
import StructuredXrayPanel from "./StructuredXrayPanel.jsx";
import ManifestXrayPanel from "./ManifestXrayPanel.jsx";

// The mounted runtime lenses, and the two things you can do to one.
//
// This existed only as a mount button for a while, which is a bad shape for the
// tier: a lens you can create and not remove accumulates, and one you cannot see
// the spec of is unfalsifiable — an empty column could be a wrong field path or an
// honest absence and there was no way to tell which from the UI.
//
// The editor is the raw spec, deliberately. A form over a shape still moving would
// constrain the shape to whatever the form knew about, and the spec is small,
// declarative and already validated on save — so the honest surface is the thing
// itself, with the validator's own problems shown when it refuses.
//
// The third action is the reading. A lens judged trace-only gets no tab — the right
// call, and also the reason mounting one used to look like it had done nothing: it
// surfaces inline in some later trace's feed and nowhere a reader could go and look.
// So each row can render the lens against the window the factory just sampled, in
// the same panels the tab would use. That makes the tier's whole point reachable:
// the spec is a claim, and the reading is the only thing that shows whether it holds.

const CARD = "rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)]";

function Chip({ label, onClick, tone = null }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded-[2px] border px-[8px] py-[2px] text-[10px] uppercase leading-[1.2] tracking-[0.04em] transition-colors cursor-pointer"
      style={tone === "danger"
        ? { borderColor: "var(--red,#c0392b)", color: "var(--red,#c0392b)" }
        : { borderColor: "var(--line)", color: "var(--muted)" }}
    >
      {label}
    </button>
  );
}

function Reading({ spec, bodies }) {
  const [showEmissions, setShowEmissions] = useState(false);
  const read = useMemo(() => {
    const { manifest, problems: errs } = compileRuntimeLens(spec);
    // A stored spec that no longer compiles is possible — the compiler's rules move
    // faster than a tenant's localStorage — and it must say so rather than render as
    // a lens that found nothing.
    if (!manifest) return { problems: errs };
    try {
      return { manifest, ...readLensOverBodies(manifest, bodies) };
    } catch (err) {
      return { problems: [`the lens threw while reading: ${err.message}`] };
    }
  }, [spec, bodies]);
  // The same ops projected through the emission contract — the shape the
  // behaviour graph ingests, identical across the declarative and artifact
  // tiers. Shown beside the reading so a spec that cannot manifest (missing
  // consumer, unstated durability) says so here, not at indexing time.
  const emissions = useMemo(() => {
    if (read.problems || !read.manifest) return null;
    try {
      const flat = read.manifest.extract
        ? (bodies || []).flatMap((b) => {
          try { return read.manifest.extract(b?.parsed?.turns || []) || []; } catch { return []; }
        })
        : [];
      return emitFromSpec(spec, flat);
    } catch (err) {
      return { manifest: null, manifestProblems: [`emission projection threw: ${err.message}`], occurrences: [], invalid: [] };
    }
  }, [read, spec, bodies]);

  if (read.problems) {
    return (
      <div className="grid gap-[4px] pt-[3px]">
        {read.problems.map((p, i) => (
          <span key={i} className="text-[10px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>{p}</span>
        ))}
      </div>
    );
  }

  return (
    <div className="grid min-w-0 gap-[6px] pt-[4px]">
      <span className={NOTE_CLASS}>
        {read.ops} operation{read.ops === 1 ? "" : "s"} out of {read.bodies} sampled
        {" "}bod{read.bodies === 1 ? "y" : "ies"}
        {read.yieldPct === null ? "" : ` · read something in ${read.yieldPct}% of them`}
        {/* Two different zeros, and they must not share a sentence: a lens that threw
            is broken, a lens that matched nothing is refuted. Reporting the first as
            the second blames the corpus for a bug in the lens. */}
        {read.threw
          ? ` · the extractor THREW on ${read.threw} of them, so this reading is incomplete for a reason that is not the corpus`
          : ""}
        {read.ops === 0 && !read.threw
          ? " This lens found nothing in its source corpus. Consider unmounting it."
          : ""}
      </span>
      {read.ops === 0 ? null : (
        <div className="min-w-0 rounded-[2px] border border-[color:var(--line-soft)]">
          {read.structure
            ? <StructuredXrayPanel lens={read.manifest} structure={read.structure} />
            : <ManifestXrayPanel lens={read.manifest} scan={{ status: "done" }} built={read.built} />}
        </div>
      )}
      {emissions ? (
        <div className="grid min-w-0 gap-[4px]">
          <span className="flex flex-wrap items-baseline gap-[8px]">
            <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)]">
              emission contract
            </span>
            <span className={NOTE_CLASS}>
              {emissions.manifestProblems.length
                ? `Cannot add this spec to the behavior graph: ${emissions.manifestProblems.length} problem${emissions.manifestProblems.length === 1 ? "" : "s"}`
                : `${emissions.occurrences.length} occurrence${emissions.occurrences.length === 1 ? "" : "s"}`
                  + (emissions.invalid.length ? ` · ${emissions.invalid.length} INVALID` : "")
                  + ` · version ${emissions.manifest.version}`}
            </span>
            {emissions.manifestProblems.length === 0 && emissions.occurrences.length ? (
              <Chip
                label={showEmissions ? "Hide occurrences" : "Occurrences"}
                onClick={() => setShowEmissions((v) => !v)}
              />
            ) : null}
          </span>
          {emissions.manifestProblems.map((p, i) => (
            <span key={i} className="text-[10px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>{p}</span>
          ))}
          {emissions.invalid.slice(0, 3).map((r, i) => (
            <span key={`inv${i}`} className="text-[10px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>
              occurrence {r.index}: {r.problems.join("; ")}
            </span>
          ))}
          {showEmissions ? (
            <pre className="m-0 max-h-[240px] overflow-auto rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px] py-[6px] font-mono text-[10px] leading-[1.5] text-[color:var(--muted)]">
              {JSON.stringify(emissions.occurrences.slice(0, 3), null, 2)}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function LensRow({ spec, bodies, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [reading, setReading] = useState(false);
  const [draft, setDraft] = useState("");
  const [problems, setProblems] = useState([]);
  // Unmount is confirmed rather than immediate. It is not destructive — the lens can
  // be mounted again — but it silently changes what every trace's residual counts,
  // and a stray click that changes a measurement is worth one extra step.
  const [confirming, setConfirming] = useState(false);

  const fields = Object.entries(spec.fields || {});
  // The door names its precision: the skeleton is the shape claimed, and the
  // `where` discriminator — when the claim was a subset — is the meaning.
  // Every door variant must resolve to a string here; a spec shape this line
  // does not know renders as a broken spec on every mount.
  const shell = spec.match?.shell;
  const shapes = Array.isArray(shell?.skeletons) ? shell.skeletons : null;
  const door = shell
    ? `shell: ${shapes ? `${shapes[0]}${shapes.length > 1 ? ` (+${shapes.length - 1} observed shape${shapes.length > 2 ? "s" : ""})` : ""}` : shell.skeleton || (shell.programs || []).join(", ") || shell.pattern}`
      + (shell.where ? ` · where /${shell.where}/` : "")
    : `tools: ${(spec.match?.tools || []).join(", ") || spec.match?.pattern}`;

  const startEdit = () => {
    setDraft(JSON.stringify(spec, null, 2));
    setProblems([]);
    setEditing(true);
  };

  const save = () => {
    let parsed;
    try {
      parsed = JSON.parse(draft);
    } catch (err) {
      // A JSON error and a contract error are different problems and must not share
      // a message — one is a typo, the other is a lens that would not work.
      setProblems([`not valid JSON: ${err.message}`]);
      return;
    }
    if (parsed.id !== spec.id) {
      setProblems(["The ID cannot be changed here. Unmount the lens, then mount it again with the new ID."]);
      return;
    }
    const { ok, problems: errs } = saveRuntimeLensSpec(null, parsed);
    if (!ok) { setProblems(errs); return; }
    setEditing(false);
    onChanged();
  };

  return (
    <div className="grid min-w-0 gap-[5px] border-t border-[color:var(--line-soft)] px-[10px] py-[7px] first:border-t-0">
      <span className="flex min-w-0 flex-wrap items-baseline gap-[8px]">
        <span className="min-w-0 truncate font-mono text-[11px] text-[color:var(--ink)]">{spec.id}</span>
        <span
          className="shrink-0 rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.06em]"
          style={spec.tab === false
            ? { borderColor: "var(--line)", color: "var(--muted)" }
            : { borderColor: "var(--green)", color: "var(--ink)" }}
        >
          {spec.tab === false ? "trace only" : "scope tab"}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-[6px]">
          {bodies?.length
            ? <Chip label={reading ? "Hide reading" : "Reading"} onClick={() => setReading((v) => !v)} />
            : null}
          {editing ? null : <Chip label="Edit" onClick={startEdit} />}
          {confirming
            ? (
              <>
                <Chip
                  label="Confirm unmount"
                  tone="danger"
                  onClick={() => { deleteRuntimeLensSpec(null, spec.id); onChanged(); }}
                />
                <Chip label="Keep" onClick={() => setConfirming(false)} />
              </>
            )
            : <Chip label="Unmount" tone="danger" onClick={() => setConfirming(true)} />}
        </span>
      </span>

      <span className={NOTE_CLASS}>{door} · grouped by {spec.group?.by}</span>
      <span className="flex min-w-0 flex-wrap gap-[4px]">
        {fields.map(([name, path]) => (
          <span
            key={name}
            className="rounded-[2px] border border-[color:var(--line)] px-[5px] py-[1px] font-mono text-[10px] text-[color:var(--muted)]"
          >
            {name} ← {path}
          </span>
        ))}
      </span>
      {/* The lens's proof obligations — the semantics it claims, as the probes
          it re-runs on every op. Named here so a reader knows what the ✓/✗ in
          the reading below are testing. */}
      {Array.isArray(spec.checks) && spec.checks.length ? (
        <span className={NOTE_CLASS}>
          Checks: {spec.checks.map((c) => `${c.name} (${c.on})`).join(" · ")}. Rechecked on every claimed call.
        </span>
      ) : null}
      {/* Carried from the verdict that drafted it, so a lens stays reviewable
          against whether anyone ever used it. */}
      {spec.draftedFrom?.falsifier ? (
        <span className={NOTE_CLASS}>Invalid when: {spec.draftedFrom.falsifier}</span>
      ) : null}

      {reading ? <Reading spec={spec} bodies={bodies} /> : null}

      {editing ? (
        <div className="grid min-w-0 gap-[5px] pt-[3px]">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            rows={16}
            className="w-full min-w-0 resize-y rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[8px] py-[6px] font-mono text-[10.5px] leading-[1.5] text-[color:var(--ink)]"
          />
          {problems.map((p, i) => (
            <span key={i} className="text-[10px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>{p}</span>
          ))}
          <span className="flex flex-wrap items-center gap-[8px]">
            <Chip label="Save" onClick={save} />
            <Chip label="Cancel" onClick={() => { setEditing(false); setProblems([]); }} />
            <span className={NOTE_CLASS}>
              Saved through the same validator a mount uses, so a spec that would not work is refused here.
            </span>
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default function MountedLensesSection({ bodies = [] }) {
  // Re-read on every change rather than holding a copy: the store is the source of
  // truth and a mount from the verdict cards above lands in it too.
  const [tick, setTick] = useState(0);
  const specs = listRuntimeLensSpecs();
  const onChanged = () => setTick((n) => n + 1);

  return (
    <section className="grid min-w-0 gap-[8px]" data-tick={tick}>
      <h3 className="m-0 text-[11px] font-medium uppercase leading-[1.1] tracking-[0.06em] text-[color:var(--eyebrow)]">
        Mounted
      </h3>
      {specs.length ? (
        <>
          <span className={NOTE_CLASS}>
            {specs.length} runtime lens{specs.length === 1 ? "" : "es"} on this tenant. They read alongside the
            built-in catalog and count toward every trace&apos;s claimed work, so unmounting one changes what
            the per-trace feed reports as residual.
          </span>
          <div className={`${CARD} grid min-w-0`}>
            {specs.map((spec) => (
              <LensRow key={spec.id} spec={spec} bodies={bodies} onChanged={onChanged} />
            ))}
          </div>
        </>
      ) : (
        // The stage renders empty rather than vanishing: this is where the loop
        // closes, and a pipeline whose last stop disappears reads as unfinished
        // rather than as not-yet-used. Scoped to LENSES — behaviour artifacts
        // render beside this section and must not be called "nothing".
        <span className={NOTE_CLASS}>
          No declarative drafts are mounted. Build lens creates a full lens; declarative mount provides a regex-only preview without a model call.
        </span>
      )}
    </section>
  );
}
